import { Anchor, Flex, Paper, Stack, Text, Title } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FeaturesTable from "../../../newPricing/FeaturesTable";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { getProducts, subscribe } from "../../../../../api/SubscriptionAPI";
import {
  useProfileStore,
  useSessionStore,
} from "../../../../../stores/UserStore";
import PagePermission from "../../../../../components/hooks/usePagePermission";
import { updateSelf } from "../../../../../api/UserAPI";
import ChangeValidation from "../../../newPricing/ChangeValidation";
import RequiredActions from "../../../account/RequiredActions/RequiredActions";
import TrialElement from "./TrialElement";
import PaidElement from "./PaidElement";
import { appHost } from "../../../../../functions/appHost";

const SubscriptionPage = () => {
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);
  const [linkedin, setLinkedin] = useState(1);
  const [email, setEmail] = useState(1);
  const [enrich, setEnrich] = useState(1);
  const [mobile, setMobile] = useState(0);
  const [reccurence, setRecurrence] = useState(
    profile?.subscriptions?.recurring_interval === "year" ? "annual" : "monthly"
  );
  const [opened, setOpened] = useState(false);
  const [actionRequiredError, setActionRequiredError] = useState(false);
  const [actionRequiredEmails, setActionRequiredEmails] = useState(0);
  const [actionRequiredLinkedins, setActionRequiredLinkedins] = useState(0);

  const [defaultValues, setDefaultValues] = useState({
    email: null,
    linkedin: null,
    enrich: null,
    mobile: null,
  });

  const defaultValuesStart = useMemo(() => {
    const userProducts = profile?.subscriptions?.products;

    if (userProducts?.length) {
      return {
        email:
          userProducts.find((x) => x.sub_type === "smtp")?.quantity * 5 || 0,
        linkedin:
          userProducts.find((x) => x.sub_type === "linkedin")?.quantity * 1 ||
          0,
        enrich:
          userProducts.find((x) => x.sub_type === "enrich")?.quantity * 1000 ||
          0,
        mobile:
          userProducts.find((x) => x.sub_type === "mobile")?.quantity * 100 ||
          0,
      };
    }

    return {
      email: profile?.permissions.find(
        (x) => x.name === "maxIntegrationsEmailSMTP"
      )?.default_value,
      linkedin: profile?.permissions.find(
        (x) => x.name === "maxIntegrationsLinkedin"
      )?.default_value,
      enrich: profile?.permissions.find(
        (x) => x.name === "maxEmailsGeneratedPerMonth"
      )?.default_value,
      mobile: profile?.permissions.find(
        (x) => x.name === "maxMobilePhonesGeneratedPerMonth"
      )?.default_value,
    };
  }, [profile?.permissions, profile?.subscriptions?.products]);

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (loading) {
      getProducts()
        .then((r) => {
          setProducts(r.data.products_list);
          updateSelf(true).finally(() => {
            setLoading(false);
          });
        })
        .catch(() => setLoading(false));
    }
  }, [loading]);

  const productsMap = useMemo(() => {
    let email_product, linkedin_product, enrich_product, mobile_product;
    if (reccurence === "annual") {
      email_product = products.find(
        (x) => x.sub_type === "smtp" && x.billing_interval === "year"
      );
      linkedin_product = products.find(
        (x) => x.sub_type === "linkedin" && x.billing_interval === "year"
      );
      enrich_product = products.find(
        (x) => x.sub_type === "enrich" && x.billing_interval === "year"
      );
      mobile_product = products.find(
        (x) => x.sub_type === "mobile" && x.billing_interval === "year"
      );
    } else {
      email_product = products.find(
        (x) => x.sub_type === "smtp" && x.billing_interval === "month"
      );
      linkedin_product = products.find(
        (x) => x.sub_type === "linkedin" && x.billing_interval === "month"
      );
      enrich_product = products.find(
        (x) => x.sub_type === "enrich" && x.billing_interval === "month"
      );
      mobile_product = products.find(
        (x) => x.sub_type === "mobile" && x.billing_interval === "month"
      );
    }

    return {
      email_product,
      linkedin_product,
      enrich_product,
      mobile_product,
    };
  }, [products, reccurence]);

  const defaultPrice = useMemo(() => {
    let email_price = productsMap?.email_product?.price_amount || 0;

    let linkedin_price = productsMap?.linkedin_product?.price_amount || 0;

    let enrich_price = productsMap?.enrich_product?.price_amount || 0;

    let mobile_price = productsMap?.mobile_product?.price_amount || 0;

    let emailCost = (email_price / 5) * defaultValuesStart?.email;
    let linkedinCost = linkedin_price * defaultValuesStart?.linkedin;
    let enrichCost = (enrich_price / 1000) * defaultValuesStart?.enrich;
    let mobileCost = (mobile_price / 100) * defaultValuesStart?.mobile;

    return emailCost + linkedinCost + enrichCost + mobileCost;
  }, [
    defaultValuesStart?.email,
    defaultValuesStart?.enrich,
    defaultValuesStart?.linkedin,
    defaultValuesStart?.mobile,
    productsMap?.email_product?.price_amount,
    productsMap?.enrich_product?.price_amount,
    productsMap?.linkedin_product?.price_amount,
    productsMap?.mobile_product?.price_amount,
  ]);

  const price = useMemo(() => {
    let email_price = productsMap?.email_product?.price_amount || 0;

    let linkedin_price = productsMap?.linkedin_product?.price_amount || 0;

    let enrich_price = productsMap?.enrich_product?.price_amount || 0;

    let mobile_price = productsMap?.mobile_product?.price_amount || 0;
    

    let emailCost = defaultValues?.email
      ? (email_price / 5) * defaultValues?.email
      : email * email_price;
    let linkedinCost = defaultValues?.linkedin
      ? linkedin_price * defaultValues?.linkedin
      : linkedin * linkedin_price;
    let enrichCost = defaultValues?.enrich
      ? (enrich_price / 1000) * defaultValues?.enrich
      : enrich * enrich_price;
    let mobileCost = defaultValues?.mobile
      ? (mobile_price / 100) * defaultValues?.mobile
      : mobile * mobile_price;

    return emailCost + linkedinCost + enrichCost + mobileCost;
  }, [
    defaultValues?.email,
    defaultValues?.enrich,
    defaultValues?.linkedin,
    defaultValues?.mobile,
    email,
    enrich,
    linkedin,
    mobile,
    productsMap?.email_product?.price_amount,
    productsMap?.enrich_product?.price_amount,
    productsMap?.linkedin_product?.price_amount,
    productsMap?.mobile_product?.price_amount,
  ]);

  const handleCheckout = () => {
    if (profile?.subscriptions?.subscription_id) {
      return setOpened(true);
    }

    const reseller = useSessionStore.getState().reseller;
    setLoading(true);

    let items = [];

    if (linkedin > 0) {
      items.push({
        price_id: productsMap.linkedin_product.price_id,
        quantity: linkedin,
      });
    }
    if (email > 0) {
      items.push({
        price_id: productsMap.email_product.price_id,
        quantity: email,
      });
    }
    if (enrich > 0) {
      items.push({
        price_id: productsMap.enrich_product.price_id,
        quantity: enrich,
      });
    }
    if (mobile > 0) {
      items.push({
        price_id: productsMap.mobile_product.price_id,
        quantity: mobile,
      });
    }

    setActionRequiredError(false);
    setActionRequiredEmails(0);
    setActionRequiredLinkedins(0);

    subscribe({
      items,
      success_url: `https://${appHost(reseller)}/settings/subscription?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `https://${appHost(reseller)}/error/subscription`,
    })
      .then((r) => {
        window.open(r.data.checkout_url, "_self");
      })
      .catch((err) => {
        let message = err.response.data.state_message;
        if (message.includes("accounts_removal")) {
          setActionRequiredError(true);
          let number = err.response.data?.accounts_to_remove;
          if (message.includes("linkedin")) {
            setActionRequiredLinkedins(number);
          } else {
            setActionRequiredEmails(number);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (
      profile?.subscriptions?.active &&
      !profile?.subscriptions?.trial &&
      profile?.subscriptions?.subscription_id
    ) {
      setDefaultValues({
        email: defaultValuesStart?.email,
        linkedin: defaultValuesStart?.linkedin,
        enrich: defaultValuesStart?.enrich,
        mobile: defaultValuesStart?.mobile,
      });

      setEmail(defaultValuesStart?.email ? defaultValuesStart?.email / 5 : 0)
      setLinkedin(defaultValuesStart?.linkedin ? defaultValuesStart?.linkedin / 1 : 0)
      setEnrich(defaultValuesStart?.enrich ? defaultValuesStart?.enrich / 1000 : 0)
      setMobile(defaultValuesStart?.mobile ? defaultValuesStart?.mobile / 100 : 0)
    }
  }, [
    defaultValuesStart,
    profile?.subscriptions?.active,
    profile?.subscriptions?.trial,
    profile?.subscriptions?.subscription_id,
  ]);

  const [edited, setEdited] = useState([]);

  return (
    <Flex flex={1} h={0} style={{ overflow: "auto" }}>
      <PagePermission permission={"displayPricing"} />

      <RequiredActions
        defaultEmailsToDelete={actionRequiredEmails}
        defaultLinkedinsToDelete={actionRequiredLinkedins}
        defaultOpened={actionRequiredError}
        setOpened={setActionRequiredError}
        isError
      />

      <Stack gap={"xs"} pr={20}>
        {profile?.subscriptions?.trial ||
        !profile?.subscriptions ||
        (!profile?.subscriptions?.trial &&
          !profile?.subscriptions?.subscription_id) ||
        (profile?.subscriptions && !profile.subscriptions?.active) ? (
          <TrialElement
            loading={loading}
            reccurence={reccurence}
            setRecurrence={setRecurrence}
            linkedin={linkedin}
            setLinkedin={setLinkedin}
            email={email}
            setEmail={setEmail}
            enrich={enrich}
            setEnrich={setEnrich}
            mobile={mobile}
            setMobile={setMobile}
            price={price}
            handleCheckout={handleCheckout}
          />
        ) : (
          <PaidElement
            loading={loading}
            reccurence={reccurence}
            setRecurrence={setRecurrence}
            linkedin={linkedin}
            setLinkedin={setLinkedin}
            email={email}
            setEmail={setEmail}
            enrich={enrich}
            setEnrich={setEnrich}
            mobile={mobile}
            setMobile={setMobile}
            price={price}
            handleCheckout={handleCheckout}
            defaultPrice={defaultPrice}
            defaultValues={defaultValues}
            setDefaultValues={setDefaultValues}
            defaultValuesStart={defaultValuesStart}
            edited={edited}
            setEdited={setEdited}
            productsMap={productsMap}
          />
        )}

        <Paper
          withBorder
          p={"lg"}
          component={Stack}
          radius={"lg"}
          mt={"xl"}
          w={"100%"}
          gap={"xs"}
          bg={"var(--mantine-color-primary-0)"}
        >
          <Title order={5}>{lang.pricing.how_to_part.title}</Title>

          <Text>
            {lang.pricing.how_to_part.text1}{" "}
            <Anchor component={Link} to={"/resellers"}>
              {lang.pricing.how_to_part.text2}
            </Anchor>{" "}
            {lang.pricing.how_to_part.text3} <br />{" "}
            {lang.pricing.how_to_part.text4}
          </Text>
        </Paper>

        <FeaturesTable />
      </Stack>

      <ChangeValidation
        opened={opened}
        setOpened={setOpened}
        defaultValues={defaultValuesStart}
        email={email}
        enrich={enrich}
        linkedin={linkedin}
        mobile={mobile}
        price={price}
        defaultPrice={defaultPrice}
        edited={edited}
        reccurence={reccurence}
        productsMap={productsMap}
        setMainLoading={setLoading}
      />
    </Flex>
  );
};

export default SubscriptionPage;
